import { useEffect, useState } from "react";
import { KeyValueObj } from "@/common/types";
import { Select } from "../selectInput";
import Input from "../Input/Input";
import { formatToNaira, getAddress } from "@/common/utils/general";
import { formatQuantity } from "@/common/utils/quantity";
import useQuery from "@/hooks/useQuery";
import { OTHER_BASE_SETTINGS_URL } from "@/common/constants";
import { useAuthDetails } from "@/hooks/useAuthDetails";
import { AddressWrapper, RequestTableWrapper, WrapperStyle } from "./styles";
import { Button } from "@/common/components/button";

export interface ICompanyTopViewProps {
  entity: KeyValueObj;
}

export function CompanyTopView({ entity }: ICompanyTopViewProps) {
  return (
    <WrapperStyle>
      <div className="top-liner"></div>
      <div className="top-content">
        <div className="info">
          <h2 className="dash-title-lg">{entity?.name}</h2>
          <h4 className="dash-text">{entity?.corporate_address}</h4>
          <h4 className="dash-text">{entity?.email}</h4>
          <h4 className="dash-text">{entity?.phone_number}</h4>
        </div>
        <img src={entity?.company_logo?.url} alt="" />
      </div>
    </WrapperStyle>
  );
}

export interface IAddressDetailsProps {
  rfq: KeyValueObj;
  isInvoice?: boolean;
  invoiceDetails?: KeyValueObj;
  title?: string;
  isInspecting?: boolean;
  requestDate?: string;
}

export function AddressDetails({
  rfq,
  invoiceDetails,
  isInvoice,
  title,
  isInspecting,
  requestDate,
}: IAddressDetailsProps) {
  const { companyType } = useAuthDetails();
  const owner = rfq?.owner;

  function getTitle(title: string | undefined | null) {
    if (title) return title;
    if (!isInspecting && companyType?.toLowerCase() === "distributor")
      return "Quotation";
    return "Request for Quotation";
  }

  function formatToStandardDate(isoDate: string): string {
    const date = new Date(isoDate);

    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  return (
    <AddressWrapper>
      <h3 className="address-top-title">{getTitle(title)}</h3>
      <div className="addresses">
        <div className="address">
          <h3 className="address-title">BILL TO</h3>
          <div className="address-details">
            <h4 className="detail">
              Company: <span>{owner?.name}</span>
            </h4>
            <h4 className="detail">
              Address: <span>{owner?.corporate_address}</span>
            </h4>
            <h4 className="detail">
              Lga: <span>{owner?.local_govt}</span>
            </h4>
            <h4 className="detail">
              State: <span>{owner?.state}</span>
            </h4>
            <h4 className="detail">
              Country: <span>Nigeria</span>
            </h4>
            <h4 className="detail">
              Phone No: <span>{owner?.phone_number}</span>
            </h4>
            <h4 className="detail">
              Email: <span>{owner?.email}</span>
            </h4>
          </div>
        </div>
        {!isInvoice ? (
          <div className="address">
            <h3 className="address-title">SHIP TO</h3>
            <div className="address-details">
              <h4 className="detail">
                Company: <span>{owner?.name}</span>
              </h4>
              <h4 className="detail">
                Shipping Address: <span>{getAddress(rfq?.address)}</span>
              </h4>
              <h4 className="detail">
                Shipping City: <span>{rfq?.address?.city}</span>
              </h4>
              <h4 className="detail">
                State: <span>{rfq?.address?.state}</span>
              </h4>
              <h4 className="detail">
                Country: <span>Nigeria</span>
              </h4>
              <h4 className="detail">
                Phone No: <span>{owner?.phone_number}</span>
              </h4>
              <h4 className="detail">
                Email: <span>{owner?.email}</span>
              </h4>
            </div>
          </div>
        ) : null}

        <div className="address">
          {isInvoice ? (
            <>
              <h3 className="address-title">INVOICE DETAILS</h3>
              <div className="address-details">
                <h4 className="detail">
                  Invoice ID: <span>{invoiceDetails?.invoice_number}</span>
                </h4>
                <h4 className="detail">
                  Invoice Issue Date:{" "}
                  <span>{invoiceDetails?.invoice_issue_date}</span>
                </h4>
                <h4 className="detail">
                  Invoice Due Date:{" "}
                  <span>{invoiceDetails?.invoice_due_date}</span>
                </h4>
                <h4 className="detail">
                  Order ID: <span>{invoiceDetails?.order_number}</span>
                </h4>
              </div>
            </>
          ) : null}
          {!isInvoice ? (
            <>
              {" "}
              <div>
                <h4 className="detail">
                  Date:{" "}
                  <span>{formatToStandardDate(requestDate as string)}</span>
                </h4>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </AddressWrapper>
  );
}

export interface IRequestTableProps {
  rfq: KeyValueObj;
  prices?: KeyValueObj;
  subTotal?: number | string;
  logisticsCost: number | string;
  tax: number | string;
  discount: number | string;
  rowCosts?: KeyValueObj;
  rowQuantities?: KeyValueObj;
  rowStockQuantities?: KeyValueObj;
  total: number | string;
  rowTotals: KeyValueObj;
  handleInputChange?: (value: string, id: string, unitPrice: string) => void;
  handleQuantityChange?: (value: string, id: string, unitPrice: string) => void;
  handleOtherInputChange?: (type: string, value: string) => void;
  handleAccountChange?: (type: string, value: string) => void;
  terms: string;
  updateCounterPaymentTerm?: (name: string, value: any) => void;
  isViewing?: boolean;
  quotation?: KeyValueObj;
  inspecting?: boolean;
  quotationExpiry?: string;
  orderType?: string;
  orderNote?: string;
  submit?: (actionType: string) => void;
  status?: string;
  loading?: boolean;
  activeButton?: string;
}

// MED-697: a line's ordered quantity read in the product's packaging levels
// ("1 Carton"), not the base units it is priced and reserved in ("10").
//
// This table is shared by RFQs, quotations, invoices and orders. Only ORDER
// lines carry `quantity_display` today, so the fallback to the raw quantity is
// load-bearing rather than defensive - a quotation line has no packaging
// hierarchy attached and must keep rendering exactly as it did.
function quantityLabel(product: KeyValueObj) {
  return formatQuantity(product?.quantity_display, product?.quantity);
}

export function RequestTable({
  subTotal,
  tax,
  discount,
  logisticsCost,
  total,
  handleInputChange,
  handleOtherInputChange,
  rowTotals,
  handleAccountChange,
  terms,
  rfq,
  quotation,
  isViewing,
  inspecting,
  quotationExpiry,
  updateCounterPaymentTerm,
  rowQuantities,
  rowStockQuantities,
  rowCosts,
  handleQuantityChange,
  orderType,
  orderNote,
  submit,
  status,
  loading,
  activeButton,
}: IRequestTableProps) {
  const [formValues, setFormValues] = useState<KeyValueObj>({});
  const [paymentTermsList, setPaymentTermsList] = useState<KeyValueObj[]>([]);
  function updateData(name: string, value: any) {
    updateCounterPaymentTerm?.(name, value);
  }
  const products = isViewing ? quotation : rfq?.quotation_request_products;
  const { companyType } = useAuthDetails();
  const showNA =
    companyType?.toLowerCase() === "distributor" && orderType === "sent";

  const { data: baseSetting } = useQuery(OTHER_BASE_SETTINGS_URL);

  const showMutation = (): boolean => {
    return (status === "draft" && showNA) ||
      companyType.toLowerCase() === "retailer" ||
      status === "accepted" ||
      status === "processed"
      ? false
      : true;
  };

  useEffect(() => {
    const paymentTerms = baseSetting?.[0]?.values?.payment_terms?.map(
      (paymentTerm: string) => ({
        title: paymentTerm,
        id: paymentTerm,
      }),
    );

    setPaymentTermsList(paymentTerms);
  }, [baseSetting]);

  const inspectingHeaders = (
    <tr>
      <th>DESCRIPTION</th>
      <th>STRENGTH/UNIT</th>
      <th>PACKAGE TYPE</th>
      <th>QUANTITY</th>
    </tr>
  );

  const mainHeaders = (
    <tr>
      <th>DESCRIPTION</th>
      <th>STRENGTH/UNIT</th>
      <th>PACKAGE TYPE</th>
      {companyType?.toLowerCase() === "distributor" && !isViewing ? (
        <th style={{ maxWidth: "5rem" }}>STOCK QUANTITY</th>
      ) : (
        ""
      )}
      <th>QUANTITY</th>
      <th>UNIT PRICE(₦)</th>
      <th>TOTAL(₦)</th>
    </tr>
  );

  const getInspectingData = (product: KeyValueObj) => (
    <tr>
      <td>{`${product?.brand_name}(${product?.product_form || ""})`}</td>
      <td>{`${product?.product_strength || ""}${
        product?.product_strength_unit || ""
      }`}</td>
      <td>{product?.package_type || ""}</td>
      <td>{quantityLabel(product)}</td>
    </tr>
  );

  const getMainData = (product: KeyValueObj) => (
    <tr>
      <td>{`${product?.brand_name}(${product?.product_form || ""})`}</td>
      <td>{`${product?.product_strength || ""}${
        product?.product_strength_unit || ""
      }`}</td>
      <td>{product?.package_type || ""}</td>
      {companyType?.toLowerCase() === "distributor" && !isViewing ? (
        <td>{rowStockQuantities?.[product?.id]}</td>
      ) : (
        ""
      )}
      <td>
        {isViewing ? (
          quantityLabel(product)
        ) : (
          <input
            onChange={(event) =>
              handleQuantityChange?.(
                event.target.value,
                product?.id,
                rowCosts?.[product?.id],
              )
            }
            type="number"
            value={rowQuantities?.[product?.id] ?? 0}
            max={product?.quantity}
          />
        )}
      </td>
      <td>
        {isViewing ? (
          formatToNaira(product?.price_per_unit) || "N/A"
        ) : (
          <input
            onChange={(event) =>
              handleInputChange?.(
                event.target.value,
                product?.id,
                rowQuantities?.[product?.id],
              )
            }
            value={rowCosts?.[product?.id] ?? 0}
            type="number"
          />
        )}
      </td>
      <td>
        {product?.price_per_unit && product?.quantity
          ? formatToNaira(
              isViewing
                ? product?.price_per_unit * product?.quantity
                : isNaN(rowTotals?.[product?.id])
                  ? 0
                  : rowTotals?.[product?.id],
            )
          : "N/A"}
      </td>
    </tr>
  );

  return (
    <RequestTableWrapper>
      <table>
        <thead>
          {!isViewing && inspecting ? inspectingHeaders : mainHeaders}
        </thead>
        <tbody>
          {products?.map((product: KeyValueObj) => {
            return !isViewing && inspecting
              ? getInspectingData(product)
              : getMainData(product);
          })}
        </tbody>
      </table>
      {inspecting ? null : (
        <div className="other-wrapper">
          <div className="other-container">
            {isViewing ? (
              <div className="account-details viewing">
                <div className="account-detail">
                  <h3 className="account-title">Payment Term</h3>
                  <h4>{rfq?.payment_term}</h4>
                </div>

                <div className="account-detail">
                  <h3 className="account-title">Remark</h3>

                  {isViewing ? (
                    <>
                      <h4 style={{ width: "100%" }}> {rfq?.buyer_note}</h4>
                      <h4 style={{ width: "100%" }}> {orderNote}</h4>
                    </>
                  ) : (
                    <textarea
                      value={terms}
                      onChange={(event) =>
                        handleAccountChange?.("terms", event.target.value)
                      }
                      style={{ width: "100%" }}
                    ></textarea>
                  )}
                </div>

                <div className="account-detail">
                  <h3 className="account-title">Expiry Date</h3>
                  <h4>{quotationExpiry}</h4>
                </div>
              </div>
            ) : null}

            <div className="other-details">
              <div className="quote-detail">
                <h3>Sub Total</h3>
                <h4>
                  {showNA && isNaN(subTotal as number)
                    ? "N/A"
                    : formatToNaira(subTotal)}
                </h4>
              </div>
              <div className="quote-detail">
                <h3>Discount(%)</h3>
                {isViewing ? (
                  <h4>{showNA ? "N/A" : discount}</h4>
                ) : (
                  <input
                    type="number"
                    value={discount}
                    onChange={(event) =>
                      handleOtherInputChange?.("discount", event.target.value)
                    }
                  />
                )}
              </div>
              <div className="quote-detail">
                <h3>Tax(%)</h3>
                {isViewing ? (
                  <h4>{showNA ? "N/A" : tax}</h4>
                ) : (
                  <input
                    type="tax"
                    value={tax}
                    onChange={(event) =>
                      handleOtherInputChange?.("tax", event.target.value)
                    }
                  />
                )}
              </div>
              <div className="quote-detail">
                <h3>Logistics</h3>

                {isViewing ? (
                  <h4>{showNA ? "N/A" : formatToNaira(logisticsCost)}</h4>
                ) : (
                  <input
                    type="number"
                    value={logisticsCost}
                    onChange={(event) =>
                      handleOtherInputChange?.(
                        "logisticsCost",
                        event.target.value,
                      )
                    }
                  />
                )}
              </div>
              <div className="quote-detail">
                <h3>Total</h3>
                {isViewing ? (
                  <h4>{formatToNaira(total) || "N/A"}</h4>
                ) : (
                  <h4>{formatToNaira(isNaN(total as number) ? 0 : total)}</h4>
                )}
              </div>
            </div>
          </div>
          {submit && showMutation() ? (
            <div className="other-action">
              <Button
                onClick={() => submit("Accept")}
                type="submit"
                primary={true}
                disabled={loading}
                loading={activeButton === "Accept"}
              >
                Accept
              </Button>
              <Button
                onClick={() => submit("Reject")}
                disabled={loading}
                secondary={true}
                loading={activeButton === "Reject"}
              >
                Decline
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {inspecting || isViewing ? null : (
        <div className="account-container">
          <div className="account-details">
            <div className="account-detail">
              <h3 className="account-title">Payment Term</h3>
              <h4>{rfq?.payment_term}</h4>
            </div>

            <div className="account-detail">
              <h3 className="account-title">Remark</h3>
              <textarea
                value={terms}
                onChange={(event) =>
                  handleAccountChange?.("terms", event.target.value)
                }
                style={{ width: "100%" }}
              ></textarea>
            </div>
          </div>
          {isViewing ? null : (
            <div className="counter-options">
              <Select
                options={paymentTermsList}
                placeholder={"Select payment term"}
                prompt={"Counter Payment Term"}
                fullWidth={true}
                shortWidth={false}
                name={"payment_term"}
                indexLevel={0}
                formValues={formValues}
                setFormValues={setFormValues}
                updateData={updateData}
                returnId
              />
              <Input
                prompt="Quotation Expiry Date"
                placeholder="Select quotation expiry date"
                fullWidth
                name={"expiration_date"}
                inputType="date"
                updateData={updateData}
                updateValidity={() => {}}
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </div>
          )}
        </div>
      )}
    </RequestTableWrapper>
  );
}
